import React, { useState, useEffect } from 'react';
import { Modal, Button, Dropdown, Message, Form } from 'semantic-ui-react';

const SubscriptionSelectionModal = ({
    open,
    onClose,
    onConfirm,
    subscriptions,
    requestData,
    linodeEnabled = false
}) => {

    const [selectedProvider, setSelectedProvider] = useState('azure');
    const [selectedSubscription, setSelectedSubscription] = useState('');
    const [error, setError] = useState(null);
    const [displaySubscriptions, setDisplaySubscriptions] = useState([]);
    const [tenantId, setTenantId] = useState(null);
    const [selectedTenant, setSelectedTenant] = useState('');
    const [selectedImageVersion, setSelectedImageVersion] = useState(null); // Start empty, 'old' for 2.6.2, 'new' for 2.6.34

    // Check if single tenancy CUST/PROD/POC auto-provisioning (used to disable dropdowns)
    // Only applies if auto-provisioning is actually enabled for this request
    const isSingleTenancyCustOrProdOrPoc = React.useMemo(() => {
        if (!requestData) return false;
        // First check if auto-provisioning is enabled for this request
        const isAutoProvision = requestData?.formData?.autoProvision === true;
        if (!isAutoProvision) return false;
        
        const isSingleTenancy = !requestData?.formData?.isMultiTenancySetup && !requestData?.formData?.requestForm3;
        const category = requestData?.category || requestData?.formData?.category;
        const categoryUpper = category ? category.toUpperCase() : '';
        const isCustOrProdOrPoc = categoryUpper === 'CUST' || categoryUpper === 'PROD' || categoryUpper === 'POC';
        return isSingleTenancy && isCustOrProdOrPoc;
    }, [requestData]);

    // Provider options (Linode gated per-user)
    const providerOptions = React.useMemo(() => {
        const options = [{ key: 'azure', text: 'Microsoft Azure', value: 'azure' }];
        if (linodeEnabled) {
            options.push({ key: 'linode', text: 'Linode', value: 'linode' });
        }
        return options;
    }, [linodeEnabled]);

    // If Linode is disabled for this user, force provider back to Azure
    useEffect(() => {
        if (!linodeEnabled && selectedProvider === 'linode') {
            setSelectedProvider('azure');
        }
    }, [linodeEnabled, selectedProvider]);

    // Image version options
    // NOTE: values remain 'old' and 'new' for logic; only labels are updated
    const imageVersionOptions = [
        { key: 'old', text: 'Platform Version 2.6.2', value: 'old' },
        { key: 'new', text: 'Platform Version 2.6.3_4', value: 'new' }
    ];

    // Extract tenant information
    const tenant = requestData?.formData?.tenant;

    // Tenant options for poc.crashplan.com + EastUS
    const tenantOptions = [
        {
            key: 'crashplan',
            text: 'CrashPlan Tenant',
            value: 'crashplan'
        },
        {
            key: 'parablu',
            text: 'Parablu Tenant',
            value: 'parablu'
        }
    ];

    // Get subscriptions based on tenant selection
    const getSubscriptionsForTenant = (tenantChoice) => {
        if (tenantChoice === 'crashplan') {
            // Use existing CPG-POC-Subscription
            return [{
                id: process.env.REACT_APP_CPG_POC_SUBSCRIPTION_ID,
                name: 'CPG-POC-Subscription',
                tenantId: process.env.REACT_APP_POC_TENANT_ID,
                clientId: process.env.REACT_APP_CPG_POC_CLIENT_ID
            }];
        } else if (tenantChoice === 'parablu') {
            // Use POC Subscription for Parablu Tenant
            return [{
                id: process.env.REACT_APP_POC_SUBSCRIPTION_ID,
                name: 'Parablu POC Demo Subscription',
                tenantId: process.env.REACT_APP_PARABLU_TENANT_ID,  // Changed from REACT_APP_PARABLU_TENANT_ID to REACT_APP_TENANT_ID
                clientId: process.env.REACT_APP_CLIENT_ID
            }];
        }
        return [];
    };

    // Check if we should show tenant selection
    const shouldShowTenantSelection = () => {
        return tenant === "poc.crashplan.com" &&
            requestData?.formData?.location?.location === "EastUS";
    };

    // Reset image version when modal opens
    useEffect(() => {
        if (open) {
            setSelectedImageVersion(null);
        }
    }, [open]);

    // Set up subscriptions based on provider and tenant when component mounts or requestData changes
    useEffect(() => {
        if (!requestData) return;

        // Reset subscription selection when provider changes
        setSelectedSubscription('');
        setError(null);

        if (selectedProvider === 'azure') {
            // Special case: poc.crashplan.com + EastUS requires tenant selection first
            if (tenant === "poc.crashplan.com" && requestData?.formData?.location?.location === "EastUS") {
                // Reset tenant selection and wait for user choice
                setSelectedTenant('');
                setDisplaySubscriptions([]);
                setTenantId(null);
                return; // Exit early, let tenant selection drive subscription options
            }

            // Handle other Azure cases with existing logic
            if (tenant === "poc.crashplan.com" || requestData?.formData?.tenantInfo?.id === process.env.REACT_APP_POC_TENANT_ID) {
                // For poc.crashplan.com (other locations), only show CPG-POC-Subscription
                const pocSubscription = {
                    id: process.env.REACT_APP_CPG_POC_SUBSCRIPTION_ID,
                    name: 'CPG-POC-Subscription',
                    tenantId: process.env.REACT_APP_POC_TENANT_ID,
                    clientId: process.env.REACT_APP_CPG_POC_CLIENT_ID
                };
                setDisplaySubscriptions([pocSubscription]);
                setSelectedSubscription(pocSubscription.id);
                setTenantId(pocSubscription.tenantId);
            } else if (tenant === "demo.crashplan.com" || requestData?.formData?.tenantInfo?.id === process.env.REACT_APP_DEMO_TENANT_ID) {
                // For demo.crashplan.com, only show CPG-Demo-Subscription
                const demoSubscription = {
                    id: process.env.REACT_APP_CPG_DEMO_SUBSCRIPTION_ID,
                    name: 'CPG-Demo-Subscription',
                    tenantId: process.env.REACT_APP_DEMO_TENANT_ID,
                    clientId: process.env.REACT_APP_CPG_DEMO_CLIENT_ID
                };
                setDisplaySubscriptions([demoSubscription]);
                setSelectedSubscription(demoSubscription.id);
                setTenantId(demoSubscription.tenantId);
            } else {
                // Existing Azure subscription logic for *.parablu.com
                let subscriptionsToUse = subscriptions;

                if (!subscriptionsToUse || subscriptionsToUse.length === 0) {
                    subscriptionsToUse = [
                        {
                            id: process.env.REACT_APP_MACC_SUBSCRIPTION_ID,
                            name: 'MACC May 2023 - April 2028 Subscription',
                            tenantId: process.env.REACT_APP_PARABLU_TENANT_ID
                        },
                        {
                            id: process.env.REACT_APP_CPG_TEST_1_SUBSCRIPTION_ID,
                            name: 'CPG_TEST_1 Subscription',
                            tenantId: process.env.REACT_APP_PARABLU_TENANT_ID
                        },
                        {
                            id: process.env.REACT_APP_POC_SUBSCRIPTION_ID,
                            name: 'Parablu POC Demo Subscription',
                            tenantId: process.env.REACT_APP_PARABLU_TENANT_ID
                        },
                        {
                            id: process.env.REACT_APP_DEV_QA_SUBSCRIPTION_ID,
                            name: 'Parablu Dev QA Subscription',
                            tenantId: process.env.REACT_APP_PARABLU_TENANT_ID
                        },
                        {
                            id: process.env.REACT_APP_DEVOPS_SUBSCRIPTION_ID,
                            name: 'Parablu DevOps Subscription',
                            tenantId: process.env.REACT_APP_PARABLU_TENANT_ID
                        }
                    ];
                }

                const subscriptionsWithTenants = subscriptionsToUse.map(sub => {
                    if (sub.id === process.env.REACT_APP_MACC_SUBSCRIPTION_ID) {
                        return {
                            ...sub,
                            tenantId: process.env.REACT_APP_PARABLU_TENANT_ID,
                            clientId: process.env.REACT_APP_CLIENT_ID
                        };
                    } else if (sub.id === process.env.REACT_APP_CPG_POC_SUBSCRIPTION_ID) {
                        return {
                            ...sub,
                            tenantId: process.env.REACT_APP_POC_TENANT_ID,
                            clientId: process.env.REACT_APP_CPG_POC_CLIENT_ID
                        };
                    } else if (sub.id === process.env.REACT_APP_CPG_DEMO_SUBSCRIPTION_ID) {
                        return {
                            ...sub,
                            tenantId: process.env.REACT_APP_DEMO_TENANT_ID,
                            clientId: process.env.REACT_APP_CPG_DEMO_CLIENT_ID
                        };
                    } else if (sub.id === process.env.REACT_APP_CPG_TEST_1_SUBSCRIPTION_ID) {
                        return {
                            ...sub,
                            tenantId: process.env.REACT_APP_PARABLU_TENANT_ID,
                            clientId: process.env.REACT_APP_CPG_CLIENT_ID
                        };
                    }
                    return {
                        ...sub,
                        tenantId: process.env.REACT_APP_PARABLU_TENANT_ID,
                        clientId: process.env.REACT_APP_CLIENT_ID
                    };
                });

                // Check if single tenancy CUST/PROD/POC auto-provisioning - filter subscriptions accordingly
                // Only applies if auto-provisioning is actually enabled for this request
                const isAutoProvision = requestData?.formData?.autoProvision === true;
                const isSingleTenancy = !requestData?.formData?.isMultiTenancySetup && !requestData?.formData?.requestForm3;
                const category = requestData?.category || requestData?.formData?.category;
                const categoryUpper = category ? category.toUpperCase() : '';
                const isCustOrProd = categoryUpper === 'CUST' || categoryUpper === 'PROD';
                const isPoc = categoryUpper === 'POC';
                
                let finalSubscriptions = subscriptionsWithTenants;
                if (isAutoProvision && isSingleTenancy && isCustOrProd) {
                    // Filter to only show MACC subscription for single tenancy CUST/PROD auto-provisioning
                    const maccSubscription = subscriptionsWithTenants.find(
                        sub => sub.id === process.env.REACT_APP_MACC_SUBSCRIPTION_ID
                    );
                    if (maccSubscription) {
                        finalSubscriptions = [maccSubscription];
                        setSelectedSubscription(maccSubscription.id);
                        setTenantId(maccSubscription.tenantId);
                    }
                } else if (isAutoProvision && isSingleTenancy && isPoc) {
                    // Filter to only show Parablu POC Demo Subscription for single tenancy POC auto-provisioning
                    const pocSubscription = subscriptionsWithTenants.find(
                        sub => sub.id === process.env.REACT_APP_POC_SUBSCRIPTION_ID
                    );
                    if (pocSubscription) {
                        finalSubscriptions = [pocSubscription];
                        setSelectedSubscription(pocSubscription.id);
                        setTenantId(pocSubscription.tenantId);
                    }
                } else if (subscriptionsWithTenants && subscriptionsWithTenants.length === 1) {
                    setSelectedSubscription(subscriptionsWithTenants[0].id);
                    setTenantId(subscriptionsWithTenants[0].tenantId);
                }

                setDisplaySubscriptions(finalSubscriptions);
            }
        } else if (selectedProvider === 'linode') {
            // For Linode, show the 3 different accounts with region based on request location
            const requestLocation = requestData?.formData?.location?.location || requestData?.formData?.location;
            let linodeRegion = 'ap-west'; // default
            let displayLocation = 'Central India'; // default display

            // Map Azure locations to Linode regions
            if (requestLocation) {
                switch (requestLocation) {
                    case 'CentralIndia':
                        linodeRegion = 'ap-west';
                        displayLocation = 'Central India';
                        break;
                    case 'EastUS':
                        linodeRegion = 'us-east';
                        displayLocation = 'East US';
                        break;
                    case 'WestUS3':
                        linodeRegion = 'us-west';
                        displayLocation = 'West US3';
                        break;
                    case 'WestUS':
                        linodeRegion = 'us-west';
                        displayLocation = 'West US';
                        break;
                    case 'UAENorth':
                        linodeRegion = 'eu-west';
                        displayLocation = 'UAE North';
                        break;
                    default:
                        linodeRegion = 'ap-west';
                        displayLocation = 'Central India';
                }
            } else {
                linodeRegion = 'ap-west';
                displayLocation = 'Central India';
            }

            const linodeAccounts = [
                {
                    id: 'linode-poc',
                    name: 'Linode POC Account',
                    region: linodeRegion,
                    location: displayLocation
                },
                {
                    id: 'linode-devqa',
                    name: 'Linode DEV/QA Account',
                    region: linodeRegion,
                    location: displayLocation
                },
                {
                    id: 'linode-cust',
                    name: 'Linode CUST Account',
                    region: linodeRegion,
                    location: displayLocation
                }
            ];
            
            // Check if single tenancy CUST/PROD/POC auto-provisioning - filter accounts accordingly
            // Only applies if auto-provisioning is actually enabled for this request
            const isAutoProvision = requestData?.formData?.autoProvision === true;
            const isSingleTenancy = !requestData?.formData?.isMultiTenancySetup && !requestData?.formData?.requestForm3;
            const category = requestData?.category || requestData?.formData?.category;
            const categoryUpper = category ? category.toUpperCase() : '';
            const isCustOrProd = categoryUpper === 'CUST' || categoryUpper === 'PROD';
            const isPoc = categoryUpper === 'POC';
            
            let finalLinodeAccounts = linodeAccounts;
            if (isAutoProvision && isSingleTenancy && isCustOrProd) {
                // Filter to only show Linode CUST Account for single tenancy CUST/PROD auto-provisioning
                const custAccount = linodeAccounts.find(acc => acc.id === 'linode-cust');
                if (custAccount) {
                    finalLinodeAccounts = [custAccount];
                    setSelectedSubscription(custAccount.id);
                } else {
                    setSelectedSubscription('');
                }
            } else if (isAutoProvision && isSingleTenancy && isPoc) {
                // Filter to only show Linode POC Account for single tenancy POC auto-provisioning
                const pocAccount = linodeAccounts.find(acc => acc.id === 'linode-poc');
                if (pocAccount) {
                    finalLinodeAccounts = [pocAccount];
                    setSelectedSubscription(pocAccount.id);
                } else {
                    setSelectedSubscription('');
                }
            } else {
                setSelectedSubscription('');
            }
            
            setDisplaySubscriptions(finalLinodeAccounts);
            setTenantId(null);
        }
    }, [requestData, tenant, subscriptions, selectedProvider]);

    // New useEffect to handle tenant selection for poc.crashplan.com + EastUS
    useEffect(() => {
        if (tenant === "poc.crashplan.com" &&
            requestData?.formData?.location?.location === "EastUS" &&
            selectedTenant) {

            const subscriptionsForTenant = getSubscriptionsForTenant(selectedTenant);
            setDisplaySubscriptions(subscriptionsForTenant);

            if (subscriptionsForTenant.length === 1) {
                setSelectedSubscription(subscriptionsForTenant[0].id);
                setTenantId(subscriptionsForTenant[0].tenantId);
            }
        }
    }, [selectedTenant, tenant, requestData]);

    // Update tenantId when subscription changes
    useEffect(() => {
        if (selectedSubscription) {
            const subscription = displaySubscriptions.find(sub => sub.id === selectedSubscription);
            if (subscription) {
                setTenantId(subscription.tenantId);
            }
        }
    }, [selectedSubscription, displaySubscriptions]);

    // Log state changes for debugging
    useEffect(() => {
    }, [selectedSubscription, tenantId, displaySubscriptions]);

    const handleConfirm = () => {
        if (!selectedSubscription) {
            setError('Please select a subscription/configuration');
            return;
        }

        if (selectedProvider === 'linode') {
            // For Linode, pass the selected account info
            const selectedAccount = displaySubscriptions.find(acc => acc.id === selectedSubscription);
            onConfirm(selectedSubscription, null, null, selectedProvider, selectedAccount, null, selectedImageVersion);
        } else {
            // Existing Azure logic
            const selectedSubscriptionObject = displaySubscriptions.find(sub => sub.id === selectedSubscription);
            const clientId = selectedSubscriptionObject?.clientId || null;
            onConfirm(selectedSubscription, tenantId, clientId, selectedProvider, null, null, selectedImageVersion);
        }
    };

    const handleProviderChange = (e, { value }) => {
        setSelectedProvider(value);
        setError(null);
    };

    const handleDropdownChange = (e, { value }) => {
        setSelectedSubscription(value);
        setError(null);
    };

    // Create options for the subscription/configuration dropdown
    const subscriptionOptions = displaySubscriptions.map(sub => ({
        key: sub.id,
        text: selectedProvider === 'linode' ?
            `${sub.name} (${sub.location} → ${sub.region})` :
            `${sub.name} (${sub.id})`,
        value: sub.id,
        content: (
            <div>
                <strong>{sub.name}</strong>
                <div style={{ fontSize: '0.9em', color: '#666' }}>
                    {selectedProvider === 'linode' ? (
                        <span>Location: {sub.location} → Linode Region: {sub.region}</span>
                    ) : (
                        <>
                            {sub.id}
                            {sub.tenantId && <span> | Tenant: {sub.tenantId}...</span>}
                        </>
                    )}
                </div>
            </div>
        )
    }));

    return (
        <Modal
            open={open}
            onClose={onClose}
            size="small"
        >
            <Modal.Header>Select Cloud Provider & Configuration</Modal.Header>
            <Modal.Content>
                {error && (
                    <Message negative>
                        <Message.Header>Error</Message.Header>
                        <p>{error}</p>
                    </Message>
                )}

                <p>Please select the cloud provider and configuration for deploying resources:</p>

                {isSingleTenancyCustOrProdOrPoc && (
                    <Message warning>
                        <Message.Header>Auto-Provisioning Configuration</Message.Header>
                        <p>
                            For single tenancy {requestData?.category || requestData?.formData?.category} auto-provisioning requests, 
                            the subscription/configuration is automatically selected and cannot be changed.
                        </p>
                    </Message>
                )}

                {requestData && (
                    <Message info>
                        <Message.Header>Request Details</Message.Header>
                        <p><strong>Cloud Name:</strong> {requestData.formData?.cloudName}</p>
                        <p><strong>Location:</strong> {requestData.formData?.location?.location}</p>
                        <p><strong>Category:</strong> {requestData.category}</p>
                        <p><strong>Tenant:</strong> {tenant}</p>
                        {tenantId && <p><strong>Tenant ID:</strong> {tenantId}</p>}
                    </Message>
                )}

                <Form>
                    <Form.Field>
                        <label>Cloud Provider</label>
                        <Dropdown
                            placeholder="Select Provider"
                            fluid
                            selection
                            options={providerOptions}
                            onChange={handleProviderChange}
                            value={selectedProvider}
                        />
                    </Form.Field>

                    {/* Show tenant selection for Azure poc.crashplan.com + EastUS */}
                    {selectedProvider === 'azure' && shouldShowTenantSelection() && (
                        <Form.Field>
                            <label>Tenant Selection</label>
                            <Dropdown
                                placeholder="Select Tenant"
                                fluid
                                selection
                                options={tenantOptions}
                                onChange={(e, { value }) => {
                                    setSelectedTenant(value);
                                    setSelectedSubscription('');
                                    setError(null);
                                }}
                                value={selectedTenant}
                            />
                        </Form.Field>
                    )}

                    {/* Show subscription/configuration dropdown when available */}
                    {displaySubscriptions.length > 0 && (
                        <Form.Field>
                            <label>{selectedProvider === 'linode' ? 'Configuration' : 'Subscription'}</label>
                            <Dropdown
                                placeholder={selectedProvider === 'linode' ? 'Select Configuration' : 'Select Subscription'}
                                fluid
                                selection
                                options={subscriptionOptions}
                                onChange={handleDropdownChange}
                                value={selectedSubscription}
                                disabled={displaySubscriptions.length === 1 || isSingleTenancyCustOrProdOrPoc}
                            />
                        </Form.Field>
                    )}

                    {/* Images selection */}
                    <Form.Field>
                        <label>Images</label>
                        <Dropdown
                            placeholder="Select Image Version"
                            fluid
                            selection
                            options={imageVersionOptions}
                            onChange={(e, { value }) => {
                                setSelectedImageVersion(value);
                                setError(null);
                            }}
                            value={selectedImageVersion || ''}
                        />
                    </Form.Field>
                </Form>

                {/* Show message when waiting for tenant selection */}
                {selectedProvider === 'azure' && shouldShowTenantSelection() && !selectedTenant && (
                    <Message info>
                        <Message.Header>Tenant Selection Required</Message.Header>
                        <p>Please select a tenant to see available subscriptions.</p>
                    </Message>
                )}

            </Modal.Content>
            <Modal.Actions>
                <Button onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    positive
                    onClick={handleConfirm}
                    disabled={!selectedSubscription || (selectedProvider === 'azure' && shouldShowTenantSelection() && !selectedTenant)}
                >
                    Confirm
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default SubscriptionSelectionModal;